import React, { Component } from "react";
import { Link, withRouter } from "react-router-dom";
import axios from "axios";
import { Store } from "react-notifications-component";
import { ArrowLeft, Mail, User, MessageSquare, Calendar, Shield, CheckCircle2, AlertCircle } from "lucide-react";

import AdminPageShell from "./AdminPageShell";
import setAuthToken from "../../utils/setAuthToken";
import { formatDateTime } from "../../utils/dateUtils";

import {
  PageHeader,
  Card,
  Button,
  Spinner,
  EmptyState,
  Alert,
  DescriptionList,
  ContactStatusBadge,
} from "../../design-system";

class ContactDetail extends Component {
  constructor(props) {
    super(props);
    this.state = { contact: null, loading: true };
  }

  componentDidMount() {
    this.loadContact();
  }

  loadContact = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (token) setAuthToken(token);
      const { id } = this.props.match.params;
      const response = await axios.get(`/api/contacts/${id}`);
      this.setState({ contact: response.data.contact, loading: false });
    } catch (error) {
      console.error("Error loading contact:", error);
      this.setState({ loading: false });
      Store.addNotification({
        title: "Erreur",
        message: error.response?.data?.message || "Impossible de charger le contact",
        type: "danger", insert: "top", container: "top-right", dismiss: { duration: 3000 },
      });
      if (error.response?.status === 404) {
        setTimeout(() => this.props.history.push("/dashboard/contacts"), 2000);
      }
    }
  };

  render() {
    const { contact, loading } = this.state;

    if (loading) {
      return (
        <AdminPageShell pageTitle="Détail du contact">
          <div className="flex items-center justify-center py-20 text-mute"><Spinner size="lg" /></div>
        </AdminPageShell>
      );
    }

    if (!contact) {
      return (
        <AdminPageShell pageTitle="Détail du contact">
          <EmptyState
            title="Contact non trouvé"
            description="Le contact demandé n'existe pas ou a été supprimé."
            action={<Button as={Link} to="/dashboard/contacts">Retour à la liste</Button>}
          />
        </AdminPageShell>
      );
    }

    const recaptchaTone =
      contact.recaptchaScore != null && contact.recaptchaScore < 0.5 ? "text-danger" : "text-success";

    return (
      <AdminPageShell pageTitle="Détail du contact" breadcrumbLast={contact.subject || contact.name}>
        <PageHeader
          eyebrow="— Contacts"
          title={contact.subject || contact.name}
          description={`De ${contact.name} <${contact.email}>`}
          badge={<ContactStatusBadge status={contact.status} />}
          actions={
            <Button
              as={Link}
              to="/dashboard/contacts"
              variant="secondary"
              leftIcon={<ArrowLeft className="w-4 h-4" strokeWidth={1.8} />}
            >
              Retour à la liste
            </Button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <Card.Header><Card.Title>Message</Card.Title></Card.Header>
              <div className="prose-sm max-w-none text-text leading-relaxed space-y-3">
                {contact.message.split("\n").map((line, i) => (
                  <p key={i} className="m-0">{line || " "}</p>
                ))}
              </div>
            </Card>

            {contact.status === "FAILED" && contact.errorMessage && (
              <Alert variant="danger" title="Message d'erreur">
                <pre className="font-mono text-xs whitespace-pre-wrap mt-1">{contact.errorMessage}</pre>
              </Alert>
            )}
          </div>

          <div className="space-y-5">
            <Card>
              <Card.Header><Card.Title>Informations</Card.Title></Card.Header>
              <DescriptionList orientation="vertical" items={[
                {
                  term: "Nom",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      {contact.name}
                    </span>
                  ),
                },
                {
                  term: "Email",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <a href={`mailto:${contact.email}`} className="underline-offset-2 hover:underline">{contact.email}</a>
                    </span>
                  ),
                },
                {
                  term: "Sujet",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      {contact.subject || "—"}
                    </span>
                  ),
                },
                {
                  term: "Reçu le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(contact.createdAt)}</span>
                    </span>
                  ),
                },
                ...(contact.sentAt ? [{
                  term: "Envoyé le",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-success" strokeWidth={1.8} />
                      <span className="font-mono text-xs">{formatDateTime(contact.sentAt)}</span>
                    </span>
                  ),
                }] : []),
                ...(contact.recaptchaScore != null ? [{
                  term: "reCAPTCHA",
                  description: (
                    <span className="inline-flex items-center gap-1.5">
                      <Shield className="w-3.5 h-3.5 text-mute" strokeWidth={1.8} />
                      <span className={`font-mono tabular-nums font-semibold ${recaptchaTone}`}>
                        {contact.recaptchaScore.toFixed(2)}
                      </span>
                    </span>
                  ),
                }] : []),
              ]} />
            </Card>
          </div>
        </div>
      </AdminPageShell>
    );
  }
}

export default withRouter(ContactDetail);
